import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, XCircle, Info, CheckCircle2 } from "lucide-react";
import type { Database } from "@/integrations/supabase/types";

type Finding = Database["public"]["Tables"]["sentinel_findings"]["Row"];

const SEVERITY_BADGE: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  critical: "destructive", high: "default", medium: "secondary", low: "outline",
};
const SEVERITY_ICON = { critical: XCircle, high: AlertTriangle, medium: Info, low: CheckCircle2 };

interface FindingCardProps {
  finding: Finding;
  onClick?: () => void;
}

export function FindingCard({ finding, onClick }: FindingCardProps) {
  const Icon = SEVERITY_ICON[finding.severity as keyof typeof SEVERITY_ICON] || Info;
  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={onClick}>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-start gap-3">
          <Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge variant={SEVERITY_BADGE[finding.severity] || "secondary"} className="text-xs">{finding.severity}</Badge>
              <Badge variant="outline" className="text-xs">{finding.dimension}</Badge>
              <Badge variant="outline" className="text-xs">{finding.category}</Badge>
            </div>
            <p className="text-sm font-medium text-foreground">Score: {Number(finding.score).toFixed(1)}</p>
            {finding.remediation_hint && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{finding.remediation_hint}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">{new Date(finding.created_at).toLocaleString("pt-BR")}</p>
          </div>
        </div>
        {finding.score_breakdown && Object.keys(finding.score_breakdown as Record<string, unknown>).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {Object.entries(finding.score_breakdown as Record<string, number>).slice(0, 5).map(([key, val]) => (
              <span key={key} className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                {key}: {typeof val === "number" ? val.toFixed(1) : String(val)}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
